/**
 * Version Check Core Logic
 * Handles version comparison and update orchestration
 */

import { store } from "../store";
import packageInfo from "../../package.json";
import {
  createBlockingOverlay,
  updateOverlayCountdown,
  removeUpdateOverlay,
  UPDATE_COUNTDOWN_SECONDS,
  COUNTDOWN_INTERVAL_MS,
} from "./versionCheckUI";

// Constants
export const CURRENT_VERSION = packageInfo.version;
const UPDATE_CHECK_INTERVAL_MS = 5000;

// State management
let isAlreadyUpdating = false;

/**
 * Compares two semantic versions with base-36 encoded major version
 * @param version1 - First version string (e.g., "v2.10.0")
 * @param version2 - Second version string (e.g., "v2.8.11")
 * @param base - Base for parsing major version (default: 36)
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
function compareVersions(
  version1: string,
  version2: string,
  base: number = 36
): -1 | 0 | 1 {
  const v1Parts = version1.split(".");
  const v2Parts = version2.split(".");

  // Compare major versions (base 36)
  const v1Major = parseInt(v1Parts[0], base);
  const v2Major = parseInt(v2Parts[0], base);

  if (v1Major !== v2Major) {
    return v1Major > v2Major ? 1 : -1;
  }

  // Compare minor/patch versions (base 10)
  const v1Minor = v1Parts.slice(1).map((part) => parseInt(part, 10));
  const v2Minor = v2Parts.slice(1).map((part) => parseInt(part, 10));

  const maxLength = Math.max(v1Minor.length, v2Minor.length);

  for (let i = 0; i < maxLength; i++) {
    const part1 = v1Minor[i] || 0;
    const part2 = v2Minor[i] || 0;

    if (part1 !== part2) {
      return part1 > part2 ? 1 : -1;
    }
  }

  return 0;
}

/**
 * Checks if the application is currently busy with file operations
 * @returns true if there are active uploads or downloads
 */
function isApplicationBusy(): boolean {
  const state = store.getState();
  const activeUploads = state.upload.counter.currentlyUploadingFiles;
  const activeThumbUploads = state.media.isThumbnailUploading;
  const isEditPageChanged = state.media.isEditPageChanged;

  return activeUploads > 0 || activeThumbUploads || isEditPageChanged;
}

/**
 * Clears browser cache and service workers before reloading
 */
async function clearCacheAndReload(): Promise<void> {
  try {
    // Clear all Cache Storage
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      console.log("Cache Storage cleared");
    }

    // Unregister all Service Workers
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
      console.log("Service Workers unregistered");
    }

    // Force fresh reload with cache-busting
    const url = new URL(window.location.href);
    window.location.href = url.toString();
  } catch (err) {
    console.error("Error during cache clear & reload:", err);
    // Fallback to normal reload
    window.location.reload();
  }
}

/**
 * Shows countdown overlay and triggers reload after countdown
 */
function showUpdateCountdownAndReload(): void {
  let remainingSeconds = UPDATE_COUNTDOWN_SECONDS;
  let countdownInterval: NodeJS.Timer | null = null;

  const cleanup = () => {
    if (countdownInterval) clearInterval(countdownInterval);
    removeUpdateOverlay();
  };

  const onCancel = () => {
    cleanup();
    // Keep isAlreadyUpdating = true to prevent re-opening in this session
    // If you want to allow re-checking, set isAlreadyUpdating = false;
    // but typically 'Cancel' implies 'Not now, stop bothering me'.
  };

  const onReload = () => {
    cleanup();
    clearCacheAndReload();
  };

  createBlockingOverlay(remainingSeconds, onCancel, onReload);

  countdownInterval = setInterval(() => {
    remainingSeconds--;

    if (remainingSeconds > 0) {
      updateOverlayCountdown(remainingSeconds);
    } else {
      if (countdownInterval) clearInterval(countdownInterval);
      const message = document.getElementById("app-update-message");
      if (message) message.innerText = "Reloading application...";
      clearCacheAndReload();
    }
  }, COUNTDOWN_INTERVAL_MS);
}

/**
 * Recursively checks if application is busy and triggers update when safe
 */
function triggerUpdateWhenSafe(): void {
  if (isApplicationBusy()) {
    setTimeout(triggerUpdateWhenSafe, UPDATE_CHECK_INTERVAL_MS);
    return;
  }

  showUpdateCountdownAndReload();
}

/**
 * Checks if a new version is available and triggers update if needed
 * @param versions - Comma-separated version string (e.g., "1.0.0,v2.1.0")
 */
export function checkAndReloadIfNewVersion(versions: string): void {
  // Guard clauses
  if (isAlreadyUpdating || !versions) {
    return;
  }

  // Extract latest version (CMS version is second item)
  const versionParts = versions.split(",");
  const latestVersion = versionParts[1];

  if (!latestVersion) {
    console.warn("No CMS version found in version string");
    return;
  }

  // Check if update is needed
  const comparisonResult = compareVersions(latestVersion, CURRENT_VERSION);

  if (comparisonResult > 0) {
    isAlreadyUpdating = true;
    triggerUpdateWhenSafe();
  }
}

/**
 * Resets the update flag (useful for testing or error recovery)
 */
export function resetUpdateFlag(): void {
  isAlreadyUpdating = false;
}
